import type {
	Product,
	ProductItem
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import { BASKET_ITEM_INFO_CODES } from "~/templates/breadandboxers/utils/basketItemInfoCodes";

export interface GtmItem {
	item_id?: number | string;
	item_group_id?: string;
	item_name?: string;
	item_brand?: string;
	item_category?: string | number;
	item_color?: string;
	item_size?: string;
	image_url?: string;
	product_url?: string;
	price: number;
	quantity: number;
	in_stock?: boolean;
	google_business_vertical?: string;
}

export type GtmTrackableItem =
	| Product
	| ProductItem
	| BasketItem
	| FilteredProductItem
	| Partial<FilteredProductItem>
	| Partial<BasketItem>;

export interface CreateGtmItemContext {
	// CDN base URL used to build absolute image_url from ImageKey.
	cdnBaseUrl?: string;
	// Site origin used to build absolute product_url from UniqueName.
	origin?: string;
}

const pickImageKey = (item: GtmTrackableItem): string | undefined => {
	// Prefer the canonical CDN GUID keys (VariantImageKey for variants, then
	// ImageKey). The legacy Image / ThumbnailImage / LargeImage fields can
	// contain raw filenames that don't resolve against the Norce CDN
	// (e.g. "103white-1.jpg"), so they're only used as a last-resort fallback.
	if (
		"VariantImageKey" in item &&
		typeof (item as { VariantImageKey?: string | null }).VariantImageKey ===
			"string" &&
		(item as { VariantImageKey: string }).VariantImageKey
	)
		return (item as { VariantImageKey: string }).VariantImageKey;
	if ("ImageKey" in item && typeof item.ImageKey === "string" && item.ImageKey)
		return item.ImageKey;
	if ("Image" in item && typeof item.Image === "string" && item.Image)
		return item.Image;
	if (
		"ThumbnailImage" in item &&
		typeof item.ThumbnailImage === "string" &&
		item.ThumbnailImage
	)
		return item.ThumbnailImage;
	if (
		"LargeImage" in item &&
		typeof item.LargeImage === "string" &&
		item.LargeImage
	)
		return item.LargeImage;
	return undefined;
};

const pickSlug = (item: GtmTrackableItem): string | undefined => {
	if (
		"VariantUniqueName" in item &&
		typeof (item as { VariantUniqueName?: string }).VariantUniqueName ===
			"string"
	) {
		return (item as { VariantUniqueName: string }).VariantUniqueName;
	}
	if (
		"UniqueName" in item &&
		typeof (item as { UniqueName?: string }).UniqueName === "string"
	) {
		return (item as { UniqueName: string }).UniqueName;
	}
	return undefined;
};

const buildImageUrl = (
	key: string | undefined,
	cdnBaseUrl: string | undefined
): string | undefined => {
	if (!key) return undefined;
	if (/^https?:\/\//i.test(key)) return key;
	if (!cdnBaseUrl) return undefined;
	return `${cdnBaseUrl.replace(/\/$/, "")}/${key.replace(/^\//, "")}`;
};

const buildProductUrl = (
	slug: string | undefined,
	origin: string | undefined
): string | undefined => {
	if (!slug) return undefined;
	if (!origin) return `/${slug}`;
	return `${origin}/${slug}`;
};

export const createGtmItem = (
	item: GtmTrackableItem,
	category?: string | number,
	defaultQuantity: number = 1,
	context: CreateGtmItemContext = {}
): GtmItem | null => {
	if (
		!("Id" in item) ||
		item.Id == null ||
		!("Name" in item) ||
		item.Name == null
	) {
		console.warn("Item is missing Id or Name test", item);
		return null;
	}

	//Frakt, do not send freight items
	if (item.Name.toLowerCase() === "frakt") {
		return null;
	}

	const brand =
		"ManufacturerName" in item && item.ManufacturerName != null
			? item.ManufacturerName
			: "Manufacturer" in item && item.Manufacturer?.Name != null
				? item.Manufacturer.Name
				: undefined;

	const inStock =
		"OnHand" in item && item.OnHand != null ? item.OnHand.Value > 0 : undefined;

	//if the item has a PartNo and GroupByKey that includes "v", it's a variant and we should try to find the variant with matching PartNo in the Variants array to get the correct price
	if (
		"PartNo" in item &&
		item.PartNo != null &&
		item.GroupByKey != null &&
		item.GroupByKey.includes("v")
	) {
		const variant =
			"Variants" in item && item.Variants != null
				? item.Variants.find((v) => v.PartNo === item.PartNo)
				: null;
		if (variant) {
			item = variant;
		}
	}

	const itemName = item.VariantName ? `${item.VariantName}` : item.Name;
	const variantParametrics =
		"VariantParametrics" in item && item.VariantParametrics != null
			? item.VariantParametrics
			: null;
	const itemColorId = variantParametrics?.find((p) => p.Code === "color")?.Id;
	const itemColor =
		variantParametrics?.find((p) => p.Code === "color")?.Value ??
		("Info" in item && item.Info != null
			? item.Info.find((i) => i.Code === BASKET_ITEM_INFO_CODES.COLOR)?.Value
			: undefined);
	const itemSize =
		variantParametrics?.find((p) => p.Code === "size")?.Value ??
		("Info" in item && item.Info != null
			? item.Info.find((i) => i.Code === BASKET_ITEM_INFO_CODES.SIZE)?.Value
			: undefined);
	const itemGroupId =
		"GroupByKey" in item && item.GroupByKey != null
			? `${item.GroupByKey}-${itemColorId}`
			: "Info" in item && item.Info != null
				? item.Info.find((i) => i.Code === BASKET_ITEM_INFO_CODES.GROUP_ID)
						?.Value
				: undefined;

	const imageUrl = buildImageUrl(pickImageKey(item), context.cdnBaseUrl);
	const productUrl = buildProductUrl(pickSlug(item), context.origin);

	return {
		item_id: item.PartNo,
		...(itemGroupId ? { item_group_id: itemGroupId } : {}),
		item_name: itemName,
		...(brand ? { item_brand: brand } : {}),
		...(category ? { item_category: category } : {}),
		...(itemColor ? { item_color: itemColor } : {}),
		...(itemSize ? { item_size: itemSize } : {}),
		...(imageUrl ? { image_url: imageUrl } : {}),
		...(productUrl ? { product_url: productUrl } : {}),
		price: item.PriceDisplay ?? item.Price ?? 0,
		in_stock: inStock,
		google_business_vertical: "retail",
		quantity:
			"Quantity" in item && item.Quantity != null
				? item.Quantity
				: defaultQuantity
	};
};
